import { useEffect, useRef, useState } from "react";
import { getSession } from '../../../../src/auth'

const HEARTBEAT_INTERVAL = 5000;   // 5s
const OWNER_TIMEOUT = 20000;       // 20s sem heartbeat = dono morto

function getOwner(OWNER_KEY) {
    const raw = localStorage.getItem(OWNER_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

function getOrCreateTabId(TABID_KEY) {
    try {
        let id = sessionStorage.getItem(TABID_KEY);
        if (!id) {
            id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            sessionStorage.setItem(TABID_KEY, id);
        }
        return id;
    } catch (e) {        
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
}

/**
 * Gera um identificador de lock por usuário/ramal.
 * Assim, dois usuários/ramais diferentes no mesmo navegador
 * não se bloqueiam.
 */
export function getWebphoneLockId() {
    const user = getSession();
    const agent = user?.profile?.agent;

    if (!agent) {
        return "unknown";
    }

    // prioridade: ramal externo, depois interno, depois id do agente
    if (agent.external_username) {
        return `ext:${agent.external_username}`;
    }

    if (agent.preferred_interface) {
        return `int:${agent.preferred_interface}`;
    }

    if (agent.id) {
        return `agent:${agent.id}`;
    }

    return "unknown";
}

export function useWebphoneTabLock() {
    const lockId = getWebphoneLockId();
    const OWNER_KEY = `kwik-webphone-owner:${lockId}`;
    const TABID_KEY = `kwik-webphone-tabid:${lockId}`;
    const [isOwner, setIsOwner] = useState(false);
    const [reason, setReason] = useState("OK"); // "OK" | "OTHER_TAB"
    const tabIdRef = useRef(null);
    const isOwnerRef = useRef(false);

    useEffect(() => {        
        if (!tabIdRef.current) {
            tabIdRef.current = getOrCreateTabId(TABID_KEY);
        }
        const tabId = tabIdRef.current;

        function tryBecomeOwner() {
            const now = Date.now();
            const currentOwner = getOwner(OWNER_KEY);

            if (!currentOwner) {                
                localStorage.setItem(
                    OWNER_KEY,
                    JSON.stringify({ tabId, ts: now })
                );
                isOwnerRef.current = true;
                setIsOwner(true);
                setReason("OK");
                return;
            }

            const isStale = now - currentOwner.ts > OWNER_TIMEOUT;

            if (currentOwner.tabId === tabId || isStale) {                
                localStorage.setItem(
                    OWNER_KEY,
                    JSON.stringify({ tabId, ts: now })
                );
                isOwnerRef.current = true;
                setIsOwner(true);
                setReason("OK");
            } else {                
                isOwnerRef.current = false;
                setIsOwner(false);
                setReason("OTHER_TAB");
            }
        }

        tryBecomeOwner();

        const hb = setInterval(() => {
            if (!isOwnerRef.current) return;

            const now = Date.now();
            const currentOwner = getOwner(OWNER_KEY);

            // se ainda sou o dono, atualizo o timestamp
            if (currentOwner && currentOwner.tabId === tabId) {
                localStorage.setItem(
                    OWNER_KEY,
                    JSON.stringify({ tabId, ts: now })
                );
            } else {
                // perdi o lock
                isOwnerRef.current = false;
                setIsOwner(false);
                setReason("OTHER_TAB");
            }
        }, HEARTBEAT_INTERVAL);

        function onStorage(e) {
            if (e.key !== OWNER_KEY) return;

            const currentOwner = getOwner(OWNER_KEY);

            if (!currentOwner) {
                tryBecomeOwner();
                return;
            }

            if (currentOwner.tabId !== tabId) {
                isOwnerRef.current = false;
                setIsOwner(false);
                setReason("OTHER_TAB");
            } else {
                isOwnerRef.current = true;
                setIsOwner(true);
                setReason("OK");
            }
        }

        window.addEventListener("storage", onStorage);

        return () => {
            clearInterval(hb);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    return { isOwner, reason };
}
